import { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { DropoffDialog } from '../components/DropoffDialog'
import { SendInInstructions } from '../components/SendInInstructions'
import { useTradeInLabelQuery } from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { VoucherStrategy, useAuthStore } from '../store/auth'
import { useCheckoutStore } from '../store/checkout'
import { useSelectionStore } from '../store/selection'
import { useSetPageTitle } from '../store/pageTitle'
import { IconArrowLeft, IconBox, IconCopy, IconGift, IconPin } from '../ui/icons'
import { useToast } from '../ui/toast'
import { money } from '../util/money'
import { useCurrencySuffix, useMoneyFmt } from '../util/useMoney'

/** Month/year for a voucher expiry, localized (e.g. "07/2027"). Null if the date is missing/invalid. */
const localeTag: Record<string, string> = { da: 'da-DK', en: 'en-GB', de: 'de-DE' }
function formatExpiry(iso: string | null | undefined, locale: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat(localeTag[locale] ?? 'en-GB', { month: '2-digit', year: 'numeric' }).format(d)
}

/**
 * Confirmation step: the trade-in has been created (on the Shipping step). The title/subtitle live
 * in the topbar; this is a full-width "what happens next" timeline. The shipping label is generated
 * asynchronously by Core, so we poll `TradeInLabel` and render the right send-in case — ported from
 * v1's TradeInPage: a drop-off **code**, a **QR** link, a printable **label** link, a home **pickup**
 * (no code), a not-ready spinner, or a generation failure.
 */
export function ConfirmPage() {
  const { t, locale } = useTranslation()
  const navigate = useNavigate()
  const tradeInId = useCheckoutStore((s) => s.tradeInId)
  const reward = useCheckoutStore((s) => s.submittedReward)
  const submittedCount = useCheckoutStore((s) => s.submittedCount)
  const resetCheckout = useCheckoutStore((s) => s.reset)
  const clearSelection = useSelectionStore((s) => s.clear)
  // Voucher-issuance policy — INSTANT shows the code here (once issued); handin/received explain the wait.
  const strategy = useAuthStore((s) => s.config.voucherStrategy) ?? VoucherStrategy.Received
  const showToast = useToast((s) => s.show)
  const fmt = useMoneyFmt()
  const suffixFor = useCurrencySuffix()
  useSetPageTitle(t('confirm.heroTitle'), t('confirm.heroBody'))

  const { data, stopPolling } = useTradeInLabelQuery({
    variables: { tradeInId: tradeInId ?? '' },
    skip: !tradeInId,
    fetchPolicy: 'no-cache',
    pollInterval: 4000,
    notifyOnNetworkStatusChange: true,
  })

  const tradeIn = data?.TradeInById
  const label = tradeIn?.label
  const dropOffPoints = (tradeIn?.dropOffPoints ?? []).flatMap((d) => (d ? [d] : []))
  const isPickup =
    tradeIn?.shippingCarrier?.carrierId === 'pickup' || tradeIn?.shippingCarrier?.customCarrier === true
  // A label is "ready" once any send-in artefact (or a failure) is present.
  const labelReady = Boolean(
    label && (label.url || label.qrUrl || label.labelCode || label.labelFailed),
  )
  // The activated INSTANT voucher, once Core has issued it (created+activated shortly after submit).
  const isInstant = strategy === VoucherStrategy.Instant
  const voucher = (tradeIn?.vouchers ?? []).find((v) => v?.activated && v.code) ?? null
  // For INSTANT we also wait for the voucher; handin/received have nothing to poll for here.
  const rewardResolved = !isInstant || Boolean(voucher)
  // Stop polling once there is nothing left to wait for (label + any instant voucher).
  useEffect(() => {
    if ((isPickup || labelReady) && rewardResolved) stopPolling()
  }, [isPickup, labelReady, rewardResolved, stopPolling])

  const n = submittedCount || tradeIn?.items?.length || 0

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      showToast(t('confirm.codeCopied'))
    } catch {
      // Clipboard blocked (permissions / insecure context) — the code is still on screen to copy by hand.
    }
  }

  // The reward timeline step (last step). INSTANT reveals the voucher code once Core issues it;
  // handin/received explain when the voucher arrives (strategy-variant copy resolved by `t()`).
  const renderReward = () => {
    if (isInstant && !voucher) {
      return (
        <>
          <h3>{t('confirm.rewardTitle')}</h3>
          <div className="confirm-generating">
            <span className="spinner" aria-hidden />
            {t('confirm.voucherIssuing')}
          </div>
        </>
      )
    }
    if (isInstant && voucher) {
      const expiry = formatExpiry(voucher.expireDate, locale)
      return (
        <>
          <h3>{t('confirm.voucherReadyTitle')}</h3>
          <div className="codebox voucher-code">
            <span className="code">{voucher.code}</span>
            <button
              type="button"
              className="voucher-copy"
              onClick={() => voucher.code && copyCode(voucher.code)}
              aria-label={t('confirm.copyCode')}
            >
              <IconCopy />
            </button>
          </div>
          <p className="voucher-meta num">
            {money(voucher.value ?? 0)} {suffixFor(voucher.currency)}
            {expiry && ` · ${t('confirm.voucherExpires', { date: expiry })}`}
          </p>
        </>
      )
    }
    return (
      <>
        <h3>{t('confirm.rewardTitle')}</h3>
        <p>
          {t('confirm.rewardBodyPre')}
          <b>{fmt(reward)}</b>
          {t('confirm.rewardBodyPost')}
        </p>
      </>
    )
  }

  const startAnother = () => {
    resetCheckout()
    clearSelection()
    navigate('/items')
  }

  const packTitle = n === 1 ? t('confirm.packTitle', { count: n }) : t('confirm.packTitlePlural', { count: n })

  // No trade-in in the checkout store (direct visit / refresh with hash|browser routing, or after a
  // reset) — there's nothing to confirm, so send them back to the start of the flow.
  if (!tradeInId) return <Navigate to="/items" replace />

  return (
    <div className="step confirm">
      <div className="timeline">
        <div className="tl">
          <span className="n">
            <IconBox />
          </span>
          <div className="c">
            <h3>{packTitle}</h3>
            <p>{isPickup ? t('confirm.pickupPackBody') : t('confirm.packBody')}</p>
            <SendInInstructions
              label={label}
              isPickup={isPickup}
              whenPending={
                <div className="confirm-generating">
                  <span className="spinner" aria-hidden />
                  {t('confirm.generatingLabel')}
                </div>
              }
            />
          </div>
        </div>

        <div className="tl">
          <span className="n">
            <IconPin />
          </span>
          <div className="c">
            <h3>{isPickup ? t('confirm.pickupTitle') : t('confirm.dropTitle')}</h3>
            <p>
              {isPickup ? (
                t('confirm.pickupBody')
              ) : (
                <>
                  {t('confirm.dropBody')}
                  {dropOffPoints.length > 0 && (
                    <DropoffDialog
                      dropOffs={dropOffPoints}
                      showQr={!!label?.qrUrl}
                      trigger={
                        <button type="button" className="link">
                          {t('confirm.findShop')}
                        </button>
                      }
                    />
                  )}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="tl">
          <span className="n">
            <IconGift />
          </span>
          <div className="c">{renderReward()}</div>
        </div>
      </div>

      <div className="confirm-actions">
        <button type="button" className="btn btn-ghost" onClick={startAnother}>
          <IconArrowLeft />
          {t('confirm.startAnother')}
        </button>
      </div>
    </div>
  )
}
